import { FunctionComponent, memo, useContext, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import Skeleton from 'react-loading-skeleton';
import YouTube from 'react-youtube';
import sha1 from 'sha1';
import { UserContext } from '../../../../context';
import {
  StoryCard,
  StoryCardArticleContent,
  StoryCardPostContent,
  StoryCardTeaserImageStyle,
  StoryCardType,
} from '../../../../services';
import { useAnalytics } from '../../../hooks';
import { FileStatus } from '../../../models';
import { getCssVar, getYoutubeVideoId } from '../../../utils';
import { IconLabel } from '../../IconLabel';
import { ImageGallery, ImageGalleryItem } from '../../ImageGallery';
import { PinnedIcon } from '../../PinnedIcon';
import { UserMentionPreview } from '../../UserMentionPreview';
import { FeedCardContentCommon } from '../FeedCardContentCommon';
import { FeedCardFileError } from '../FeedCardFileError';
import { FeedCardMargin } from '../FeedCardMargin';

import classNames from 'classnames';
import classes from './FeedCardTeaser.module.scss';

interface FeedCardTeaserProps {
  card: StoryCard;
  chatId?: number;
}

export const FeedCardTeaser: FunctionComponent<FeedCardTeaserProps> = memo(({ card, chatId }) => {
  const { logClickOpenArticle } = useAnalytics();

  const { id: userId, channelId } = useContext(UserContext).userInfo.userData;

  const {
    storyId,
    id: cardId,
    type,
    content,
    isPinnedToTopOfNewsFeed,
    isPinnedToTopOfStory,
    mentionedUsers,
  } = card;

  const {
    title,
    abstract,
    sourceName,
    gallery,
    styles: { teaserImageStyle },
  } = content as StoryCardArticleContent | StoryCardPostContent;

  const { url } = content as StoryCardArticleContent;

  const youtubeVideoId = useMemo(() => getYoutubeVideoId(url), [url]);

  const iconId = useMemo(
    () => `arrow-rounded-right${type === StoryCardType.ARTICLE ? '-up' : ''}`,
    [type]
  );

  const renderLargeTeaserSource = useMemo(() => {
    if (!sourceName && !isPinnedToTopOfNewsFeed && !isPinnedToTopOfStory) {
      return null;
    }

    return (
      <div className={classes['large-teaser__content-source-wrapper']}>
        <PinnedIcon
          isPinnedToTopOfNewsFeed={isPinnedToTopOfNewsFeed}
          isPinnedToTopOfStory={isPinnedToTopOfStory}
          color={getCssVar('--card-teaser-text-color')}
          source={sourceName}
        />
        {sourceName && <div className={classes['large-teaser__content-source']}>{sourceName}</div>}
      </div>
    );
  }, [isPinnedToTopOfNewsFeed, isPinnedToTopOfStory, sourceName]);

  const renderLargeTeaserTitle = useMemo(() => {
    if (!title) {
      return null;
    }

    return (
      <div
        className={classNames(classes['large-teaser__content-title'], {
          [classes['large-teaser__content-title--mobile']]: isMobileOnly,
        })}
      >
        {title}
        <span className={classes['large-teaser__content-title-icon']}>
          <IconLabel
            iconId={iconId}
            iconSize={isMobileOnly ? 20 : 24}
            color={getCssVar('--card-teaser-icon-color')}
            singleColor
          />
        </span>
      </div>
    );
  }, [iconId, title]);

  if (Boolean(gallery.find(({ image: { status } }) => status === FileStatus.ERROR))) {
    return (
      <FeedCardMargin top right bottom left>
        <FeedCardFileError />
      </FeedCardMargin>
    );
  }

  if (Boolean(gallery.find(({ image: { status } }) => status === FileStatus.PROCESSING))) {
    return (
      <FeedCardMargin top right bottom left>
        <Skeleton height={'5rem'} />
      </FeedCardMargin>
    );
  }

  const image = gallery[0]?.image;

  if (!image) {
    return (
      <FeedCardContentCommon
        type={type}
        isPinnedToTopOfNewsFeed={isPinnedToTopOfNewsFeed}
        isPinnedToTopOfStory={isPinnedToTopOfStory}
        source={sourceName}
        title={title}
        text={abstract}
        chatId={chatId}
        mentionedUsers={mentionedUsers}
      />
    );
  }

  const imageGalleryItems: ImageGalleryItem[] = gallery.map(({ image }) => {
    return { image };
  });

  switch (true) {
    case Boolean(teaserImageStyle === StoryCardTeaserImageStyle.STANDARD && youtubeVideoId):
      return (
        <>
          <YouTube
            videoId={youtubeVideoId}
            opts={{ width: '100%' }}
            onPlay={() =>
              logClickOpenArticle({
                user_Id: sha1(`${userId}`),
                channel_id: channelId,
                story_id: storyId,
                item_id: cardId,
                time_stamp: new Date().toISOString(),
              })
            }
          />
          <FeedCardContentCommon
            type={type}
            isPinnedToTopOfNewsFeed={isPinnedToTopOfNewsFeed}
            isPinnedToTopOfStory={isPinnedToTopOfStory}
            source={sourceName}
            title={title}
            text={abstract}
            chatId={chatId}
            mentionedUsers={mentionedUsers}
          />
        </>
      );
    case Boolean(teaserImageStyle === StoryCardTeaserImageStyle.STANDARD || chatId):
      return (
        <>
          <ImageGallery items={imageGalleryItems} />
          <FeedCardContentCommon
            type={type}
            isPinnedToTopOfNewsFeed={isPinnedToTopOfNewsFeed}
            isPinnedToTopOfStory={isPinnedToTopOfStory}
            source={sourceName}
            title={title}
            text={abstract}
            chatId={chatId}
            mentionedUsers={mentionedUsers}
          />
        </>
      );
    case Boolean(teaserImageStyle === StoryCardTeaserImageStyle.SMALL_WITH_TEXT):
      return (
        <FeedCardContentCommon
          type={type}
          isPinnedToTopOfNewsFeed={isPinnedToTopOfNewsFeed}
          isPinnedToTopOfStory={isPinnedToTopOfStory}
          source={sourceName}
          title={title}
          text={abstract}
          teaser={image}
          chatId={chatId}
          mentionedUsers={mentionedUsers}
        />
      );
    case Boolean(teaserImageStyle === StoryCardTeaserImageStyle.SMALL_WITHOUT_TEXT):
      return (
        <FeedCardContentCommon
          type={type}
          isPinnedToTopOfNewsFeed={isPinnedToTopOfNewsFeed}
          isPinnedToTopOfStory={isPinnedToTopOfStory}
          source={sourceName}
          title={title}
          teaser={image}
          chatId={chatId}
          mentionedUsers={mentionedUsers}
        />
      );
    case Boolean(teaserImageStyle === StoryCardTeaserImageStyle.BIG_WITHOUT_TEXT):
      return (
        <>
          <UserMentionPreview
            mentionedUsers={mentionedUsers}
            className={classes['mentions']}
            openProfile
          />
          <div className={classes['large-teaser']}>
            <ImageGallery items={imageGalleryItems} singleImageFullSize />
            <div
              className={classNames(classes['large-teaser__content'], {
                [classes['large-teaser__content--with-copy']]: image?.rightholder,
              })}
            >
              {renderLargeTeaserSource}

              {renderLargeTeaserTitle}
            </div>
          </div>
        </>
      );
    default:
      return null;
  }
});
